/**
 * OverdueContactCallSheet · iter303ak-fix42 — Overdue · T20 · Action 10
 * ─────────────────────────────────────────────────────────────────────
 * TWO-STAGE call sheet:
 *   Stage 1 — client/property panel · unpaid summary · call script · outcome pick
 *   Stage 2 — outcome-specific follow-up form (Promise / Payment plan /
 *             Voicemail / No answer / Refused) with scripted questions,
 *             auto date-stamps, recall booking, and reason capture.
 *
 * Persists nested at `overdue_contact_call.{outcome, notes, called_at, follow_up:{...}}`
 * so the T20 A20 summary letter can render outcome-specific copy.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Phone, Mail, MapPin, Calendar, DollarSign, CheckCircle2, AlertCircle, ArrowLeft, Clock, Voicemail, PhoneOff, AlertTriangle, DollarSign as DS } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (baseISO, days) => {
  const d = baseISO ? new Date(baseISO) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const nowStamp = () => new Date().toISOString();

const OUTCOMES = [
  { key: 'contacted_promise',   label: 'Contacted · Promise to pay', color: 'emerald', icon: DollarSign,   next_hint: 'Capture promised date, amount and method below.', recall_default_days: 2 },
  { key: 'contacted_needs_plan',label: 'Contacted · Needs payment plan', color: 'amber', icon: Calendar,     next_hint: 'Capture proposed plan terms, finalise at T40.', recall_default_days: 3 },
  { key: 'voicemail',           label: 'Voicemail left',             color: 'sky',      icon: Voicemail,   next_hint: 'Confirm the scripted VM you left, book callback.', recall_default_days: 2 },
  { key: 'no_answer',           label: 'No answer',                  color: 'slate',    icon: PhoneOff,    next_hint: 'Log the attempt, book a retry.', recall_default_days: 1 },
  { key: 'refused',             label: 'Client refuses to pay',      color: 'rose',     icon: AlertTriangle, next_hint: 'Capture reason + walk client through Tribunal process.', recall_default_days: 7 },
];

const PAYMENT_METHODS = ['Bank transfer', 'Cheque', 'Cash', 'Card', 'Other'];
const FREQUENCIES = ['weekly', 'fortnightly', 'monthly'];
const REFUSE_REASONS = [
  { key: 'cant_afford',   label: "Can't afford" },
  { key: 'bad_work',      label: 'Unhappy with the work' },
  { key: 'hardship',      label: 'Financial hardship / illness' },
  { key: 'no_reason',     label: 'No reason given' },
  { key: 'other',         label: 'Other (note below)' },
];

// Seeded scripts (editable in Script Audit later)
const VM_SCRIPT_DEFAULT = (name, addr, total, phone) =>
  `Hi ${name || 'there'}, this is Trevor from Roofs To Remedy calling about the outstanding balance for the roofing work at ${addr || 'your property'}. The amount owing is $${total}. ` +
  `Please give us a call back on ${phone || 'our office number'} at your earliest convenience so we can sort this out — we'd much rather work with you than escalate. Thanks and I look forward to hearing from you.`;

const TRIBUNAL_PROCESS_SCRIPT =
  '1) We file a claim with the New Zealand Disputes Tribunal (online at disputestribunal.govt.nz). ' +
  '2) A filing fee is added to your balance ($59.44 for claims up to $2,000, more for higher amounts). ' +
  '3) The Tribunal notifies you and sets a hearing date — usually within 6–8 weeks. ' +
  '4) Interest at 10% p.a. accrues on the debt from the date of default. ' +
  '5) If the Tribunal finds in our favour, a formal order is issued; failure to pay lets us engage debt collectors — their fees are added to what you owe. ' +
  '6) An unpaid Tribunal order can affect your credit record.';

export default function OverdueContactCallSheet({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parent = useMemo(() => (capturedData?.appraisal_record || capturedData?.appraisal || {}), [capturedData]);
  const client = capturedData?.client_record || capturedData?.client || {};
  const prop = capturedData?.property_record || capturedData?.property || {};

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const payments = parent.invoiced_payments || {};
  const wexec = parent.work_execution || {};
  const depositPick = parent.per_child_deposit_pick || {};

  const unpaidChildren = acceptedChildren.filter(c => {
    const s = payments[c.child_id]?.status;
    return s !== 'paid' && s !== 'written_off';
  });
  const paidChildren = acceptedChildren.filter(c => {
    const s = payments[c.child_id]?.status;
    return s === 'paid' || s === 'written_off';
  });

  const clientName = [client.name_title, client.name_first, client.name_last].filter(Boolean).join(' ') || 'Client';
  const propAddress = [
    `${prop.property_address_num || ''} ${prop.property_address_street || ''}`.trim(),
    prop.property_address_suburb, prop.property_address_city,
  ].filter(Boolean).join(', ') || '—';
  const totalDue = unpaidChildren.reduce((a, c) => a + Number(payments[c.child_id]?.remaining || 0), 0);
  const callbackPhone = '027 XXX XXXX'; // placeholder — actual company phone comes from tokens on the letter side

  // ── Root state ──────────────────────────────────────────────
  const [outcome, setOutcome]   = useState(value?.outcome || '');
  const [notes, setNotes]       = useState(value?.notes || '');
  const [calledAt, setCalledAt] = useState(value?.called_at || todayISO());
  const [followUp, setFollowUp] = useState(value?.follow_up || {});

  const meta = OUTCOMES.find(o => o.key === outcome);

  // When outcome is picked, seed sensible defaults for that branch (once)
  useEffect(() => {
    if (!outcome) return;
    setFollowUp(prev => {
      if (prev && prev._seeded_for === outcome) return prev;
      const base = { _seeded_for: outcome, stamped_at: nowStamp() };
      if (outcome === 'contacted_promise') {
        return {
          ...base,
          payment_method: PAYMENT_METHODS[0],
          amount: totalDue ? Number(totalDue.toFixed(2)) : 0,
          promised_date: addDaysISO(null, 3),
          recall_date: addDaysISO(null, meta?.recall_default_days || 2),
          bank_details_needed: false,
          script_qs: { asked_amount: false, asked_when: false, asked_method: false, asked_details: false },
        };
      }
      if (outcome === 'contacted_needs_plan') {
        return {
          ...base,
          frequency: 'weekly',
          instalment_amount: 0,
          proposed_start: addDaysISO(null, 7),
          upfront_deposit: 0,
          recall_date: addDaysISO(null, meta?.recall_default_days || 3),
          script_qs: { asked_capacity: false, asked_start: false, asked_upfront: false },
        };
      }
      if (outcome === 'voicemail') {
        return {
          ...base,
          vm_script: VM_SCRIPT_DEFAULT(clientName, propAddress, fmt(totalDue), callbackPhone),
          time_of_call: new Date().toTimeString().slice(0, 5),
          callback_number_left: callbackPhone,
          recall_date: addDaysISO(null, meta?.recall_default_days || 2),
        };
      }
      if (outcome === 'no_answer') {
        return {
          ...base,
          time_of_call: new Date().toTimeString().slice(0, 5),
          recall_date: addDaysISO(null, meta?.recall_default_days || 1),
        };
      }
      if (outcome === 'refused') {
        return {
          ...base,
          reason: '',
          reason_note: '',
          final_request_read: false,
          tribunal_explained: false,
          recall_date: addDaysISO(null, meta?.recall_default_days || 7),
        };
      }
      return base;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);

  // Bubble up
  useEffect(() => {
    if (!onChange) return;
    onChange({ outcome, notes, called_at: calledAt, follow_up: followUp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome, notes, calledAt, followUp]);

  // Gate: continue requires outcome-specific mandatory fields
  const canContinue = useMemo(() => {
    if (!outcome) return false;
    const f = followUp || {};
    if (outcome === 'contacted_promise') return !!f.payment_method && Number(f.amount) > 0 && !!f.promised_date;
    if (outcome === 'contacted_needs_plan') return !!f.frequency && Number(f.instalment_amount) > 0 && !!f.proposed_start;
    if (outcome === 'voicemail') return !!f.vm_script && !!f.callback_number_left;
    if (outcome === 'no_answer') return !!f.recall_date;
    if (outcome === 'refused') return !!f.reason && f.final_request_read && f.tribunal_explained;
    return true;
  }, [outcome, followUp]);

  const setFu = (patch) => setFollowUp(prev => ({ ...prev, ...patch }));

  return (
    <div className="space-y-4" data-testid="wf-overdue-contact-sheet">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <Phone className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Contact Client · Call Sheet</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Use the script below while on the phone. Pick an outcome — a follow-up form appears to capture the details. The summary letter draft at the next action adapts to what you capture here.'}
            </div>
          </div>
        </div>
      </div>

      {/* Client + Property panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 border border-slate-200 rounded-lg bg-white" data-testid="wf-overdue-contact-client">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Client</div>
          <div className="text-sm font-semibold text-slate-800">{clientName}</div>
          {client.name_mobile && <div className="text-xs text-slate-600 mt-1"><Phone size={10} className="inline mr-1" />{client.name_mobile}</div>}
          {client.name_phone_home && <div className="text-xs text-slate-600"><Phone size={10} className="inline mr-1" />{client.name_phone_home}</div>}
          {client.name_email && <div className="text-xs text-slate-600"><Mail size={10} className="inline mr-1" />{client.name_email}</div>}
        </div>
        <div className="p-3 border border-slate-200 rounded-lg bg-white" data-testid="wf-overdue-contact-property">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Property</div>
          <div className="text-sm text-slate-800"><MapPin size={12} className="inline mr-1" />{propAddress}</div>
          {prop.property_type && <div className="text-xs text-slate-600 mt-1">Type: {prop.property_type}</div>}
        </div>
      </div>

      {/* Unpaid summary */}
      <div className="border border-rose-300 bg-rose-50/30 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-rose-100 border-b border-rose-300 text-xs font-bold uppercase tracking-widest text-rose-900">
          Unpaid · {unpaidChildren.length} child{unpaidChildren.length === 1 ? '' : 'ren'} · Total remaining <b className="font-mono">${fmt(totalDue)}</b>
        </div>
        <div className="p-3 space-y-2">
          {unpaidChildren.map(c => {
            const pb = payments[c.child_id] || {};
            const dep = depositPick[c.child_id] || {};
            return (
              <div key={c.child_id} className="p-2 bg-white border border-slate-200 rounded"
                data-testid={`wf-overdue-contact-unpaid-${c.child_id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-semibold text-slate-800">
                    {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
                  </span>
                  {c.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{c.appraisal_no}</span>}
                  <span className="ml-auto text-xs">Due <b className="font-mono">${fmt(pb.due_amount)}</b> · Paid <b className="font-mono text-emerald-700">${fmt(pb.paid_total)}</b> · Remaining <b className="font-mono text-rose-700">${fmt(pb.remaining)}</b></span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-slate-600">
                  <span><Calendar size={10} className="inline mr-1" />Quote {c.quote_date || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Accepted {c.accepted_at?.slice(0,10) || '—'} ({c.acceptance_type || '—'})</span>
                  <span><DollarSign size={10} className="inline mr-1" />Deposit ${fmt(dep.amount || 0)} on {dep.date || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Started {wexec.started_at?.slice(0,10) || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Completed {wexec.signed_off_at?.slice(0,10) || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Invoice {parent.invoice_sent_at?.slice(0,10) || '—'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Paid children reference */}
      {paidChildren.length > 0 && (
        <div className="border border-emerald-200 bg-emerald-50/30 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-emerald-100 border-b border-emerald-300 text-xs font-bold uppercase tracking-widest text-emerald-900">
            Paid / Settled · {paidChildren.length} child{paidChildren.length === 1 ? '' : 'ren'} · reference only
          </div>
          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {paidChildren.map(c => {
              const pb = payments[c.child_id] || {};
              return (
                <div key={c.child_id} className="p-2 bg-white border border-slate-200 rounded text-xs"
                  data-testid={`wf-overdue-contact-paid-${c.child_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">
                      {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
                    </span>
                    {c.appraisal_no && <span className="text-[9px] font-bold uppercase text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1 py-0.5">A{c.appraisal_no}</span>}
                    <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full px-1.5 py-0.5">
                      {pb.status === 'written_off' ? 'Written off' : '✓ Fully paid'}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Paid <b className="font-mono">${fmt(pb.paid_total)}</b> of ${fmt(pb.due_amount)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scripted call */}
      <div className="p-4 border-2 border-sky-300 bg-sky-50/40 rounded-xl" data-testid="wf-overdue-contact-script">
        <div className="text-xs font-bold uppercase tracking-widest text-sky-900 mb-2">Call Script</div>
        <ol className="space-y-2 text-sm text-slate-800 list-decimal pl-5">
          <li>"Hello <b>{clientName}</b>, how are you today?"</li>
          <li>"I'm calling about payment for {unpaidChildren.length === 1 ? unpaidChildren[0]?.quote_type_ref || 'the roofing work' : 'the roofing work'} that was completed on <b>{wexec.signed_off_at?.slice(0,10) || 'recently'}</b>. We haven't received payment yet."</li>
          <li>"The amount required is <b>${fmt(totalDue)}</b> — are you able to make payment?"</li>
          <li>"If you need assistance to make the payment, we can arrange a time payment plan — would that help?"</li>
        </ol>
      </div>

      {/* ═══ STAGE 1 · Outcome pick ═══ */}
      {!outcome && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Call Outcome — pick one to open its follow-up form</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {OUTCOMES.map(o => {
              const Icon = o.icon;
              return (
                <button key={o.key} onClick={() => setOutcome(o.key)}
                  data-testid={`wf-overdue-contact-outcome-${o.key}`}
                  className={`text-left p-3 border-2 rounded-lg transition-all text-xs border-slate-200 bg-white hover:border-${o.color}-400`}>
                  <div className={`font-semibold text-sm text-slate-800 flex items-center gap-2`}>
                    <Icon size={14} className={`text-${o.color}-600`} /> {o.label}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{o.next_hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ STAGE 2 · Outcome-specific follow-up form ═══ */}
      {outcome && meta && (
        <div className={`p-4 border-2 rounded-xl border-${meta.color}-400 bg-${meta.color}-50/50`} data-testid={`wf-overdue-fu-${outcome}`}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className={`text-sm font-bold text-${meta.color}-900 flex items-center gap-2`}>
              {React.createElement(meta.icon, { size: 16 })} {meta.label}
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-white border border-slate-300 rounded px-1.5 py-0.5 ml-1">
                <Clock size={9} className="inline mr-0.5" />
                Stamped {followUp.stamped_at?.slice(0,16).replace('T',' ') || 'now'}
              </span>
            </div>
            <button onClick={() => { setOutcome(''); setFollowUp({}); }}
              data-testid="wf-overdue-fu-change-outcome"
              className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <ArrowLeft size={11} /> Change outcome
            </button>
          </div>

          {/* PROMISE TO PAY */}
          {outcome === 'contacted_promise' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Payment method</label>
                  <select value={followUp.payment_method || ''} onChange={(e) => setFu({ payment_method: e.target.value })}
                    data-testid="wf-fu-promise-method"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded">
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Amount ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.amount || 0}
                    onChange={(e) => setFu({ amount: Number(e.target.value) })}
                    data-testid="wf-fu-promise-amount"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Promised payment date</label>
                  <input type="date" value={followUp.promised_date || ''}
                    onChange={(e) => setFu({ promised_date: e.target.value, recall_date: addDaysISO(e.target.value, 2) })}
                    data-testid="wf-fu-promise-date"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date (auto follow-up)</label>
                  <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                    data-testid="wf-fu-promise-recall"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <label className="flex items-end gap-2 text-xs pb-1 text-slate-700">
                  <input type="checkbox" checked={!!followUp.bank_details_needed}
                    onChange={(e) => setFu({ bank_details_needed: e.target.checked })}
                    data-testid="wf-fu-promise-bank-needed" className="w-4 h-4 accent-emerald-600" />
                  Client asked for our bank details again (resend with letter)
                </label>
              </div>
              <div className="p-2 bg-white border border-emerald-200 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 mb-1">Script — tick as asked</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-700">
                  {[
                    ['asked_amount', 'What amount can you pay today or by an agreed date?'],
                    ['asked_when', 'When exactly will payment be made?'],
                    ['asked_method', 'Which method will you use (bank transfer / cheque / card)?'],
                    ['asked_details', 'Do you need our bank details again?'],
                  ].map(([k, label]) => (
                    <label key={k} className="inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={!!followUp.script_qs?.[k]}
                        onChange={(e) => setFu({ script_qs: { ...followUp.script_qs, [k]: e.target.checked } })}
                        data-testid={`wf-fu-promise-q-${k}`} className="w-3.5 h-3.5 accent-emerald-600" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* NEEDS PAYMENT PLAN */}
          {outcome === 'contacted_needs_plan' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Frequency</label>
                  <select value={followUp.frequency || 'weekly'} onChange={(e) => setFu({ frequency: e.target.value })}
                    data-testid="wf-fu-plan-freq"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded">
                    {FREQUENCIES.map(f => <option key={f} value={f}>{f[0].toUpperCase()+f.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Instalment amount ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.instalment_amount || 0}
                    onChange={(e) => setFu({ instalment_amount: Number(e.target.value) })}
                    data-testid="wf-fu-plan-amount"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Proposed start date</label>
                  <input type="date" value={followUp.proposed_start || ''} onChange={(e) => setFu({ proposed_start: e.target.value })}
                    data-testid="wf-fu-plan-start"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Up-front deposit ($)</label>
                  <input type="number" min="0" step="0.01" value={followUp.upfront_deposit || 0}
                    onChange={(e) => setFu({ upfront_deposit: Number(e.target.value) })}
                    data-testid="wf-fu-plan-deposit"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date (finalise plan at T40)</label>
                <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                  data-testid="wf-fu-plan-recall"
                  className="w-full md:w-1/3 px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
              </div>
              <div className="p-2 bg-white border border-amber-200 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1">Script — tick as asked</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-700">
                  {[
                    ['asked_capacity', 'What amount can you afford per week / fortnight / month?'],
                    ['asked_start', 'When can you start the plan?'],
                    ['asked_upfront', 'Any upfront amount you can pay today?'],
                  ].map(([k, label]) => (
                    <label key={k} className="inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={!!followUp.script_qs?.[k]}
                        onChange={(e) => setFu({ script_qs: { ...followUp.script_qs, [k]: e.target.checked } })}
                        data-testid={`wf-fu-plan-q-${k}`} className="w-3.5 h-3.5 accent-amber-600" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* VOICEMAIL LEFT */}
          {outcome === 'voicemail' && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Voicemail script — read verbatim, tweak as needed</label>
                <textarea rows={5} value={followUp.vm_script || ''} onChange={(e) => setFu({ vm_script: e.target.value })}
                  data-testid="wf-fu-vm-script"
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Time of call</label>
                  <input type="time" value={followUp.time_of_call || ''} onChange={(e) => setFu({ time_of_call: e.target.value })}
                    data-testid="wf-fu-vm-time"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Callback number left</label>
                  <input type="text" value={followUp.callback_number_left || ''} onChange={(e) => setFu({ callback_number_left: e.target.value })}
                    data-testid="wf-fu-vm-callback"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date</label>
                  <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                    data-testid="wf-fu-vm-recall"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
            </div>
          )}

          {/* NO ANSWER */}
          {outcome === 'no_answer' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Time of call</label>
                  <input type="time" value={followUp.time_of_call || ''} onChange={(e) => setFu({ time_of_call: e.target.value })}
                    data-testid="wf-fu-na-time"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Recall date</label>
                  <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                    data-testid="wf-fu-na-recall"
                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                </div>
              </div>
              <div className="text-[11px] text-slate-600 italic">
                Attempt logged. The summary letter will note that we tried to reach you today and will try again on the recall date above.
              </div>
            </div>
          )}

          {/* REFUSED */}
          {outcome === 'refused' && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Reason for refusal</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {REFUSE_REASONS.map(r => (
                    <label key={r.key} className="inline-flex items-center gap-1.5 text-xs text-slate-700 p-1.5 border border-slate-200 rounded bg-white">
                      <input type="radio" name="refuse_reason" value={r.key} checked={followUp.reason === r.key}
                        onChange={() => setFu({ reason: r.key })}
                        data-testid={`wf-fu-refuse-reason-${r.key}`} className="w-3.5 h-3.5 accent-rose-600" />
                      <span>{r.label}</span>
                    </label>
                  ))}
                </div>
                {(followUp.reason === 'other' || followUp.reason === 'no_reason') && (
                  <input type="text" placeholder="Add note / detail…"
                    value={followUp.reason_note || ''} onChange={(e) => setFu({ reason_note: e.target.value })}
                    data-testid="wf-fu-refuse-reason-note"
                    className="mt-2 w-full px-2 py-1 text-sm border border-slate-300 rounded" />
                )}
              </div>

              <div className="p-3 bg-white border-2 border-rose-300 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800 mb-1">Final Request for Payment — read to client</div>
                <p className="text-xs text-slate-700 mb-2">
                  "I understand. Before we escalate this, I want to make one final request that you settle this account today or agree a payment plan to avoid us filing this debt with the New Zealand Disputes Tribunal. If we file, you will be liable for the Tribunal fee, ongoing interest at 10% per annum from the date of default, and any collection agency fees. Is there anything at all we can work out together right now?"
                </p>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-800">
                  <input type="checkbox" checked={!!followUp.final_request_read}
                    onChange={(e) => setFu({ final_request_read: e.target.checked })}
                    data-testid="wf-fu-refuse-final-read" className="w-4 h-4 accent-rose-600" />
                  I read this final request to the client.
                </label>
              </div>

              <div className="p-3 bg-white border-2 border-rose-300 rounded">
                <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800 mb-1">Tribunal Filing Process — explain to client</div>
                <p className="text-[11px] text-slate-700 whitespace-pre-line mb-2">{TRIBUNAL_PROCESS_SCRIPT}</p>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-800">
                  <input type="checkbox" checked={!!followUp.tribunal_explained}
                    onChange={(e) => setFu({ tribunal_explained: e.target.checked })}
                    data-testid="wf-fu-refuse-tribunal-explained" className="w-4 h-4 accent-rose-600" />
                  I explained the Tribunal filing process to the client.
                </label>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-0.5">Tribunal filing deadline (last-chance recall)</label>
                <input type="date" value={followUp.recall_date || ''} onChange={(e) => setFu({ recall_date: e.target.value })}
                  data-testid="wf-fu-refuse-recall"
                  className="w-full md:w-1/3 px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
                <div className="text-[11px] text-rose-700 mt-1"><AlertCircle size={11} className="inline mr-1" />If no payment or plan agreed by this date, T50 Tribunal Prep is triggered.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Called date + notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Called date</label>
          <input type="date" value={calledAt} onChange={(e) => setCalledAt(e.target.value)}
            data-testid="wf-overdue-contact-called-date"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Notes (general)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Client on holiday until next Friday · will pay Monday"
            data-testid="wf-overdue-contact-notes"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!canContinue}
            data-testid="wf-overdue-contact-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue to Letter Draft
          </button>
        </div>
      </div>
    </div>
  );
}
